import React, { useEffect, useState } from 'react';
import { fetchActionLogs, ActionLogQuery } from '../../api/actionLogApi';
import { Input, Select, Table, DatePicker, Button, Space, Modal, Drawer, Descriptions, Tag } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';

interface ActionLog {
  _id: string;
  userId: string;
  userName?: string;
  userRole: string;
  actionType: string;
  entityType: string;
  entityId: string;
  entityName: string;
  description: string;
  beforeData?: any;
  afterData?: any;
  changes?: { field: string; oldValue: any; newValue: any }[];
  ipAddress?: string;
  userAgent?: string;
  timestamp: string;
}

const actionTypeOptions = [
  { value: '', label: 'Все действия' },
  { value: 'создание', label: 'Создание' },
  { value: 'обновление', label: 'Обновление' },
  { value: 'удаление', label: 'Удаление' },
  { value: 'вход', label: 'Вход' },
  { value: 'выход', label: 'Выход' },
  { value: 'архивирование', label: 'Архивирование' },
  { value: 'восстановление', label: 'Восстановление' },
];

const entityTypeOptions = [
  { value: '', label: 'Все сущности' },
  { value: 'пользователь', label: 'Пользователь' },
  { value: 'заказ', label: 'Заказ' },
  { value: 'транзакция', label: 'Транзакция' },
  { value: 'клиент', label: 'Клиент' },
  { value: 'касса', label: 'Касса' },
  { value: 'расход', label: 'Расход' },
  { value: 'инвентарь', label: 'Инвентарь' },
  { value: 'материал', label: 'Материал' },
  { value: 'продукт', label: 'Продукт' },
  { value: 'отчет', label: 'Отчет' },
  { value: 'зарплата', label: 'Зарплата' },
  { value: 'работник', label: 'Работник' },
  { value: 'партнер', label: 'Партнер' },
  { value: 'настройка', label: 'Настройка' },
  { value: 'заказ-наряд', label: 'Заказ-наряд' },
  { value: 'перевод средств', label: 'Перевод средств' },
  { value: 'клиент конвертации', label: 'Клиент конвертации' },
  { value: 'расход на доставку', label: 'Расход на доставку' },
  { value: 'вилка', label: 'Вилка' },
  { value: 'машина', label: 'Машина' },
  { value: 'показание счетчика', label: 'Счетчик' },
  { value: 'должность', label: 'Должность' },
  { value: 'цена', label: 'Цена' },
  { value: 'продукция', label: 'Продукция' },
  { value: 'возврат', label: 'Возврат' },
  { value: 'оборот', label: 'Оборот' },
  { value: 'вывод средств', label: 'Вывод средств' },
  { value: 'журнал работ', label: 'Журнал работ' },
  { value: 'ревизия', label: 'Ревизия' },
  { value: 'партия', label: 'Партия' },
  { value: 'список', label: 'Список' },
  { value: 'файл', label: 'Файл' },
  { value: 'филлер', label: 'Филлер' },
];

const { RangePicker } = DatePicker;

const ActionLogPage: React.FC = () => {
  const [logs, setLogs] = useState<ActionLog[]>([]);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [filters, setFilters] = useState<ActionLogQuery>({});
  const [selectedLog, setSelectedLog] = useState<ActionLog | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  const fetchLogs = async (params: ActionLogQuery = {}) => {
    setLoading(true);
    try {
      const data = await fetchActionLogs({ ...params, page, limit });
      setLogs(data.logs || []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error('Error fetching logs:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(filters);
    // eslint-disable-next-line
  }, [page, limit, filters]);

  const showDetails = (record: ActionLog) => {
    setSelectedLog(record);
    setDrawerVisible(true);
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'Не указано';
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  const columns: ColumnsType<ActionLog> = [
    { 
      title: 'Дата', 
      dataIndex: 'timestamp', 
      key: 'timestamp', 
      render: (v) => dayjs(v).format('DD.MM.YYYY HH:mm'),
      width: 150
    },
    { 
      title: 'Пользователь', 
      dataIndex: 'userName', 
      key: 'userName',
      width: 120
    },
    { 
      title: 'Роль', 
      dataIndex: 'userRole', 
      key: 'userRole',
      width: 100,
      render: (role) => <Tag color="blue">{role}</Tag>
    },
    { 
      title: 'Действие', 
      dataIndex: 'actionType', 
      key: 'actionType',
      width: 100,
      render: (action) => {
        const colors = {
          'создание': 'green',
          'обновление': 'orange',
          'удаление': 'red',
          'вход': 'blue',
          'выход': 'default'
        };
        return <Tag color={colors[action as keyof typeof colors] || 'default'}>{action}</Tag>;
      }
    },
    { 
      title: 'Сущность', 
      dataIndex: 'entityType', 
      key: 'entityType',
      width: 120
    },
    { 
      title: 'Название', 
      dataIndex: 'entityName', 
      key: 'entityName',
      width: 150
    },
    { 
      title: 'Описание', 
      dataIndex: 'description', 
      key: 'description',
      width: 200,
      ellipsis: true
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      render: (_, record) => (
        <Button 
          type="link" 
          size="small"
          onClick={() => showDetails(record)}
        >
          Подробнее
        </Button>
      ),
    },
  ];

  const handleFilterChange = (key: keyof ActionLogQuery, value: any) => {
    setPage(1);
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  return (
    <div style={{ padding: 24 }}>
      <h2>Журнал действий пользователей</h2>
      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          placeholder="Поиск по описанию/названию"
          allowClear
          onChange={e => handleFilterChange('search', e.target.value)}
          style={{ width: 220 }}
        />
        <Select
          options={actionTypeOptions}
          defaultValue=""
          style={{ width: 160 }}
          onChange={v => handleFilterChange('actionType', v)}
          placeholder="Тип действия"
        />
        <Select
          options={entityTypeOptions}
          defaultValue=""
          style={{ width: 180 }}
          onChange={v => handleFilterChange('entityType', v)}
          placeholder="Тип сущности"
        />
        <RangePicker
          format="DD.MM.YYYY"
          onChange={dates => {
            handleFilterChange('startDate', dates?.[0]?.format('YYYY-MM-DD'));
            handleFilterChange('endDate', dates?.[1]?.format('YYYY-MM-DD'));
          }}
        />
      </Space>
      <Table
        columns={columns}
        dataSource={logs}
        rowKey="_id"
        loading={loading}
        pagination={{
          current: page,
          pageSize: limit,
          total,
          onChange: setPage,
          showSizeChanger: true,
          onShowSizeChange: (_, size) => setLimit(size),
        }}
        scroll={{ x: 1000 }}
      />

      <Drawer
        title="Детали действия"
        placement="right"
        width={600}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedLog && (
          <div>
            <Descriptions column={1} bordered>
              <Descriptions.Item label="Дата">
                {dayjs(selectedLog.timestamp).format('DD.MM.YYYY HH:mm:ss')}
              </Descriptions.Item>
              <Descriptions.Item label="Пользователь">
                {selectedLog.userName || selectedLog.userId}
              </Descriptions.Item>
              {selectedLog.userName && (
                <Descriptions.Item label="ID пользователя">
                  {selectedLog.userId}
                </Descriptions.Item>
              )}
              <Descriptions.Item label="Роль">
                <Tag color="blue">{selectedLog.userRole}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Действие">
                <Tag color="orange">{selectedLog.actionType}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Сущность">
                {selectedLog.entityType}
              </Descriptions.Item>
              <Descriptions.Item label="Название">
                {selectedLog.entityName}
              </Descriptions.Item>
              <Descriptions.Item label="Описание">
                {selectedLog.description}
              </Descriptions.Item>
              {selectedLog.ipAddress && (
                <Descriptions.Item label="IP адрес">
                  {selectedLog.ipAddress}
                </Descriptions.Item>
              )}
            </Descriptions>

            {selectedLog.changes && selectedLog.changes.length > 0 && (
              <div style={{ marginTop: 24 }}>
                <h4>Изменения:</h4>
                <Table
                  dataSource={selectedLog.changes}
                  columns={[
                    { title: 'Поле', dataIndex: 'field', key: 'field' },
                    { 
                      title: 'Было', 
                      dataIndex: 'oldValue', 
                      key: 'oldValue',
                      render: (value) => <pre style={{ margin: 0, fontSize: '12px' }}>{formatValue(value)}</pre>
                    },
                    { 
                      title: 'Стало', 
                      dataIndex: 'newValue', 
                      key: 'newValue',
                      render: (value) => <pre style={{ margin: 0, fontSize: '12px' }}>{formatValue(value)}</pre>
                    },
                  ]}
                  pagination={false}
                  size="small"
                />
              </div>
            )}

            {(selectedLog.beforeData || selectedLog.afterData) && (
              <div style={{ marginTop: 24 }}>
                <h4>Данные:</h4>
                <div style={{ display: 'flex', gap: 16 }}>
                  {selectedLog.beforeData && (
                    <div style={{ flex: 1 }}>
                      <h5>До:</h5>
                      <pre style={{ 
                        background: '#f5f5f5', 
                        padding: 12, 
                        borderRadius: 4, 
                        fontSize: '12px',
                        maxHeight: 300,
                        overflow: 'auto'
                      }}>
                        {JSON.stringify(selectedLog.beforeData, null, 2)}
                      </pre>
                    </div>
                  )}
                  {selectedLog.afterData && (
                    <div style={{ flex: 1 }}>
                      <h5>После:</h5>
                      <pre style={{ 
                        background: '#f5f5f5', 
                        padding: 12, 
                        borderRadius: 4, 
                        fontSize: '12px',
                        maxHeight: 300,
                        overflow: 'auto'
                      }}>
                        {JSON.stringify(selectedLog.afterData, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </Drawer>
    </div>
  );
};

export default ActionLogPage; 